import { useCallback, useMemo, useState } from 'react'
import { Aircraft, Transaction } from '../types/intellijetnet'
import { AircraftOverride, AircraftTransaction, TransactionOverride } from '../types/database'
import { compareSerialNumbers } from '../utils/formatters'
import { getEffectiveAircraftData, getEffectiveCustomColumnValue } from '../utils/overrides'
import type { CombinedAircraftData, SortColumn, SortDirection } from '../components/market-table/types'
import type { EffectiveAircraft } from './useMarketTableData'

interface UseMarketTableSortingParams {
  tableType: 'for-sale' | 'sold' | 'removed'
  combinedAircraft: CombinedAircraftData[]
  getEffectiveAircraft: (aircraft: Aircraft) => EffectiveAircraft
  getEffectiveTransactionAircraft: (aircraft: Aircraft, aircraftTransaction?: AircraftTransaction | null) => EffectiveAircraft
  getEffectiveTransactionData: (aircraftTransaction: AircraftTransaction | null | undefined, transaction: Transaction | null | undefined) => any
  relativeValues: Map<number, number | null>
  overrides: Map<number, AircraftOverride>
  transactionOverrides: Map<string, TransactionOverride>
}

export function useMarketTableSorting({
  tableType,
  combinedAircraft,
  getEffectiveAircraft,
  getEffectiveTransactionAircraft,
  getEffectiveTransactionData,
  relativeValues,
  overrides,
  transactionOverrides,
}: UseMarketTableSortingParams) {
  const [sortBy, setSortBy] = useState<SortColumn>(
    tableType === 'sold' ? 'date_sold' : tableType === 'removed' ? 'date_removed' : 'serial'
  )
  const [sortDirection, setSortDirection] = useState<SortDirection>(
    tableType === 'sold' || tableType === 'removed' ? 'desc' : 'asc'
  )

  const sortCombinedAircraft = useCallback((data: CombinedAircraftData[]): CombinedAircraftData[] => {
    return [...data].sort((a, b) => {
      let effectiveA: EffectiveAircraft
      let effectiveB: EffectiveAircraft

      if (tableType === 'sold') {
        effectiveA = getEffectiveTransactionAircraft(a.aircraft, a.aircraftTransaction)
        effectiveB = getEffectiveTransactionAircraft(b.aircraft, b.aircraftTransaction)
      } else {
        effectiveA = getEffectiveAircraft(a.aircraft)
        effectiveB = getEffectiveAircraft(b.aircraft)
      }

      let valueA: any
      let valueB: any

      switch (sortBy) {
        case 'serial':
          const serialA = a.aircraft.sernbr || ''
          const serialB = b.aircraft.sernbr || ''
          const serialComparison = compareSerialNumbers(serialA, serialB)
          return sortDirection === 'asc' ? serialComparison : -serialComparison
        case 'year':
          valueA = effectiveA.yearmfr || 0
          valueB = effectiveB.yearmfr || 0
          break
        case 'aftt':
          valueA = effectiveA.aftt || 0
          valueB = effectiveB.aftt || 0
          break
        case 'asking':
          valueA = parseFloat(effectiveA.asking || '0')
          valueB = parseFloat(effectiveB.asking || '0')
          break
        case 'take':
          valueA = effectiveA.take_price || 0
          valueB = effectiveB.take_price || 0
          break
        case 'external_asking':
          if (tableType === 'sold') {
            const transactionOverrideA = a.aircraftTransaction ? transactionOverrides.get(a.aircraftTransaction.transaction_id) : null
            const transactionOverrideB = b.aircraftTransaction ? transactionOverrides.get(b.aircraftTransaction.transaction_id) : null
            valueA = parseFloat((transactionOverrideA?.external_asking_price ?? a.aircraftTransaction?.external_asking_price ?? a.aircraftTransaction?.asking_price ?? 0).toString())
            valueB = parseFloat((transactionOverrideB?.external_asking_price ?? b.aircraftTransaction?.external_asking_price ?? b.aircraftTransaction?.asking_price ?? 0).toString())
          } else {
            const effA = getEffectiveAircraftData(a.aircraft, overrides.get(a.aircraft.aircraft_id))
            const effB = getEffectiveAircraftData(b.aircraft, overrides.get(b.aircraft.aircraft_id))
            valueA = parseFloat((overrides.get(a.aircraft.aircraft_id)?.external_asking_price ?? effA.asking ?? 0).toString())
            valueB = parseFloat((overrides.get(b.aircraft.aircraft_id)?.external_asking_price ?? effB.asking ?? 0).toString())
          }
          break
        case 'external_take':
          if (tableType === 'sold') {
            const transactionOverrideA = a.aircraftTransaction ? transactionOverrides.get(a.aircraftTransaction.transaction_id) : null
            const transactionOverrideB = b.aircraftTransaction ? transactionOverrides.get(b.aircraftTransaction.transaction_id) : null
            valueA = transactionOverrideA?.external_take_price ?? a.aircraftTransaction?.external_take_price ?? a.aircraftTransaction?.take_price ?? 0
            valueB = transactionOverrideB?.external_take_price ?? b.aircraftTransaction?.external_take_price ?? b.aircraftTransaction?.take_price ?? 0
          } else {
            const effA = getEffectiveAircraftData(a.aircraft, overrides.get(a.aircraft.aircraft_id))
            const effB = getEffectiveAircraftData(b.aircraft, overrides.get(b.aircraft.aircraft_id))
            valueA = overrides.get(a.aircraft.aircraft_id)?.external_take_price ?? effA.take_price ?? 0
            valueB = overrides.get(b.aircraft.aircraft_id)?.external_take_price ?? effB.take_price ?? 0
          }
          break
        case 'sold':
          const effectiveDataA = getEffectiveTransactionData(a.aircraftTransaction, a.transaction)
          const effectiveDataB = getEffectiveTransactionData(b.aircraftTransaction, b.transaction)
          valueA = parseFloat(effectiveDataA.sale_price?.toString() || '0')
          valueB = parseFloat(effectiveDataB.sale_price?.toString() || '0')
          break
        case 'date_listed':
          if (tableType === 'for-sale') {
            valueA = a.source === 'offMarket' ? '1900-01-01' : (a.aircraft.datelisted || '1900-01-01')
            valueB = b.source === 'offMarket' ? '1900-01-01' : (b.aircraft.datelisted || '1900-01-01')
          } else {
            valueA = (a.transaction as Transaction)?.list_date || '1900-01-01'
            valueB = (b.transaction as Transaction)?.list_date || '1900-01-01'
          }
          break
        case 'date_sold':
          valueA = a.aircraftTransaction?.transaction_date || (a.transaction as Transaction)?.trans_date || '1900-01-01'
          valueB = b.aircraftTransaction?.transaction_date || (b.transaction as Transaction)?.trans_date || '1900-01-01'
          break
        case 'date_removed':
          valueA = a.event?.date || '1900-01-01'
          valueB = b.event?.date || '1900-01-01'
          break
        case 'relative_value':
          valueA = relativeValues.get(a.aircraft.aircraft_id) || 0
          valueB = relativeValues.get(b.aircraft.aircraft_id) || 0
          break
        case 'custom_1':
          valueA = getEffectiveCustomColumnValue(1, a.aircraft, overrides.get(a.aircraft.aircraft_id)) || ''
          valueB = getEffectiveCustomColumnValue(1, b.aircraft, overrides.get(b.aircraft.aircraft_id)) || ''
          break
        case 'custom_2':
          valueA = getEffectiveCustomColumnValue(2, a.aircraft, overrides.get(a.aircraft.aircraft_id)) || ''
          valueB = getEffectiveCustomColumnValue(2, b.aircraft, overrides.get(b.aircraft.aircraft_id)) || ''
          break
        case 'custom_3':
          valueA = getEffectiveCustomColumnValue(3, a.aircraft, overrides.get(a.aircraft.aircraft_id)) || ''
          valueB = getEffectiveCustomColumnValue(3, b.aircraft, overrides.get(b.aircraft.aircraft_id)) || ''
          break
        default:
          valueA = a.aircraft.sernbr || ''
          valueB = b.aircraft.sernbr || ''
      }

      if (typeof valueA === 'number' && typeof valueB === 'number') {
        return sortDirection === 'asc' ? valueA - valueB : valueB - valueA
      } else {
        const strA = String(valueA).toLowerCase()
        const strB = String(valueB).toLowerCase()
        const comparison = strA.localeCompare(strB)
        return sortDirection === 'asc' ? comparison : -comparison
      }
    })
  }, [sortBy, sortDirection, getEffectiveAircraft, getEffectiveTransactionAircraft, getEffectiveTransactionData, tableType, relativeValues, overrides, transactionOverrides])

  const sortedCombinedAircraft = useMemo(() => {
    return sortCombinedAircraft(combinedAircraft)
  }, [combinedAircraft, sortCombinedAircraft])

  const handleSort = useCallback((column: SortColumn) => {
    if (sortBy === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(column)
      setSortDirection('asc')
    }
  }, [sortBy])

  return {
    sortBy,
    sortDirection,
    sortedCombinedAircraft,
    handleSort,
  }
}

